import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'odelicieuse_cookie_consent';

const CookieBanner = () => {
  const { isDark } = useTheme();
  const { isStaff } = useAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  // Hide on admin routes and for staff/admin users — the banner overlaps
  // fixed action bars (e.g. "Sauvegarder" on /admin/produits/{id}) and was
  // intercepting clicks. Staff are not the consent target audience anyway.
  const suppressOnRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (suppressOnRoute || isStaff) {
      // Auto-accept silently so the banner never reappears for staff
      try {
        if (!localStorage.getItem(STORAGE_KEY)) {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ accepted: true, date: new Date().toISOString(), auto: true })
          );
        }
      } catch {}
      setVisible(false);
      return;
    }
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      if (!value) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, [suppressOnRoute, isStaff]);

  const accept = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accepted: true, date: new Date().toISOString() })
      );
    } catch {}
    setVisible(false);
  };

  const refuse = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accepted: false, date: new Date().toISOString() })
      );
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-6 sm:bottom-6 z-50 max-w-2xl mx-auto rounded-xl shadow-2xl p-5 sm:p-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{
        backgroundColor: 'var(--color-surface)',
        border: `1px solid ${isDark ? '#2a2a2a' : '#eee5d1'}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Consentement cookies"
    >
      <div className="flex items-start gap-4">
        <div
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(212, 175, 55, 0.15)' }}
        >
          <Cookie className="w-5 h-5 text-[color:var(--color-primary)]" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-heading mb-1">
            Votre vie privée compte pour nous
          </h3>
          <p className="text-sm text-muted leading-relaxed mb-4">
            Nous utilisons des cookies strictement nécessaires au fonctionnement du site
            (connexion, panier, préférences). Aucun cookie publicitaire n'est utilisé. En
            savoir plus dans notre{' '}
            <Link to="/cookies" className="text-[color:var(--color-primary)] hover:underline font-medium">
              politique de cookies
            </Link>
            .
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={accept}
              data-testid="cookie-accept-btn"
              className="px-5 py-2 rounded-lg bg-[color:var(--color-primary)] text-black font-semibold text-sm hover:bg-[color:var(--color-primary-hover)] transition-colors"
            >
              J'ai compris
            </button>
            <button
              onClick={refuse}
              data-testid="cookie-refuse-btn"
              className="px-5 py-2 rounded-lg border text-sm hover:bg-opacity-10 transition-colors"
              style={{
                borderColor: isDark ? '#2a2a2a' : '#d9c8a4',
                color: isDark ? '#c8b996' : '#6b5544',
              }}
            >
              Refuser les cookies non essentiels
            </button>
          </div>
        </div>
        <button
          onClick={refuse}
          aria-label="Fermer"
          data-testid="cookie-close-btn"
          className="flex-shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors"
        >
          <X className="w-4 h-4 text-muted" />
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
